import type { CSSProperties, MouseEvent as ReactMouseEvent } from 'react';
import { Check, ChevronDown, Plus, Search, X } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { joinCss, pickCss } from '@/shared/lib/css-utils';
import css from './creatable-multi-select.module.scss';

const PORTAL_GAP = 4;
const PORTAL_MAX_HEIGHT = 320;
const PORTAL_MIN_WIDTH = 220;
const PORTAL_EDGE_MARGIN = 8;

type PortalPosition = {
  top: number;
  left: number;
  right: number;
  width: number;
  placement: 'top' | 'bottom';
  alignRight: boolean;
};

const styles = pickCss(css, css.root);

export type CreatableMultiSelectOption = {
  label: string;
  value: string;
};

type CreatableMultiSelectProps = {
  className?: string;
  disabled?: boolean;
  id?: string;
  onChange: (values: string[]) => void;
  options: CreatableMultiSelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  // Label prefix for the "create" affordance, e.g. "Create region".
  createLabel?: string;
  size?: 'sm' | 'default';
  style?: CSSProperties;
  values: string[];
  // Render the dropdown in a body portal (z-index above modals).
  usePortal?: boolean;
};

const CheckboxIndicator = ({ checked }: { checked: boolean }) => (
  <span className={joinCss(css.checkbox, checked && css.checkboxChecked)}>
    {checked && <Check className={css.checkIcon} />}
  </span>
);

// A multi-select that also lets the user type a brand-new value (Enter, or the
// "Create …" row) when the search term matches no existing option. Selected
// values render as removable pills in the trigger. Ported from the legacy
// CreatableMultiSelect; visual structure mirrors the shared MultiSelect.
export const CreatableMultiSelect = ({
  className,
  disabled,
  id,
  onChange,
  options,
  placeholder = 'Select or create…',
  searchPlaceholder = 'Search…',
  createLabel = 'Create',
  size = 'default',
  style,
  values,
  usePortal = false,
}: CreatableMultiSelectProps) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const portalRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [portalPosition, setPortalPosition] = useState<PortalPosition | null>(
    null
  );

  const normalizedQuery = query.trim().toLowerCase();

  const visibleOptions = useMemo(() => {
    if (!normalizedQuery) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(normalizedQuery) ||
        o.value.toLowerCase().includes(normalizedQuery)
    );
  }, [options, normalizedQuery]);

  // Offer "create" only when the trimmed term matches no existing option and
  // is not already selected.
  const trimmed = query.trim();
  const canCreate =
    trimmed.length > 0 &&
    !options.some((o) => o.value.toLowerCase() === trimmed.toLowerCase()) &&
    !values.some((v) => v.toLowerCase() === trimmed.toLowerCase());

  const toggle = useCallback(
    (value: string) => {
      const next = values.includes(value)
        ? values.filter((v) => v !== value)
        : [...values, value];
      onChange(next);
    },
    [values, onChange]
  );

  const create = useCallback(
    (value: string) => {
      const v = value.trim();
      if (!v) return;
      if (!values.some((existing) => existing === v)) onChange([...values, v]);
      setQuery('');
    },
    [values, onChange]
  );

  const removeValue = useCallback(
    (value: string, e: ReactMouseEvent) => {
      e.stopPropagation();
      onChange(values.filter((v) => v !== value));
    },
    [values, onChange]
  );

  const clearAll = useCallback(
    (e: ReactMouseEvent) => {
      e.stopPropagation();
      onChange([]);
    },
    [onChange]
  );

  useEffect(() => {
    if (!open) {
      setQuery('');
      return undefined;
    }
    const t = window.setTimeout(() => searchInputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideRoot = rootRef.current?.contains(target);
      const insidePortal = portalRef.current?.contains(target);
      if (!insideRoot && !insidePortal) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !usePortal) {
      setPortalPosition(null);
      return undefined;
    }
    const compute = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const placement: PortalPosition['placement'] =
        spaceBelow < PORTAL_MAX_HEIGHT + PORTAL_GAP && spaceAbove > spaceBelow
          ? 'top'
          : 'bottom';
      const estWidth = Math.max(rect.width, PORTAL_MIN_WIDTH);
      const alignRight =
        rect.left + estWidth > window.innerWidth - PORTAL_EDGE_MARGIN;
      setPortalPosition({
        top:
          placement === 'top'
            ? rect.top - PORTAL_GAP
            : rect.bottom + PORTAL_GAP,
        left: rect.left,
        right: window.innerWidth - rect.right,
        width: rect.width,
        placement,
        alignRight,
      });
    };
    compute();
    const onScrollOrResize = () => compute();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, usePortal]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const labelFor = (value: string) =>
    options.find((o) => o.value === value)?.label ?? value;

  const dropdownContent = (
    <>
      <div className={css.searchRow}>
        <Search className={css.searchIcon} />
        <input
          className={css.searchInput}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (canCreate) create(trimmed);
            } else if (e.key === 'Backspace' && query === '' && values.length) {
              onChange(values.slice(0, -1));
            }
          }}
          placeholder={searchPlaceholder}
          ref={searchInputRef}
          type="text"
          value={query}
        />
      </div>
      <div className={css.optionList}>
        {visibleOptions.map((option) => (
          <button
            className={css.option}
            key={option.value}
            onClick={() => toggle(option.value)}
            type="button"
          >
            <CheckboxIndicator checked={values.includes(option.value)} />
            <span className={css.optionLabel}>{option.label}</span>
          </button>
        ))}
        {canCreate && (
          <button
            className={joinCss(css.option, css.optionCreate)}
            onClick={() => create(trimmed)}
            type="button"
          >
            <Plus className={css.createIcon} />
            <span className={css.optionLabel}>
              {createLabel} “{trimmed}”
            </span>
          </button>
        )}
        {visibleOptions.length === 0 && !canCreate && (
          <span className={css.empty}>No options</span>
        )}
      </div>
    </>
  );

  return (
    <div className={css.wrapper} ref={rootRef} style={style}>
      <button
        className={styles(
          { isDisabled: !!disabled, isOpen: open, size },
          className
        )}
        disabled={disabled}
        id={id}
        onClick={() => setOpen((prev) => !prev)}
        ref={triggerRef}
        type="button"
      >
        {values.length > 0 ? (
          <span className={css.pills}>
            {values.map((value) => (
              <span className={css.pill} key={value}>
                <span className={css.pillLabel}>{labelFor(value)}</span>
                <X
                  aria-label={`Remove ${labelFor(value)}`}
                  className={css.pillRemove}
                  onClick={(e) => removeValue(value, e)}
                />
              </span>
            ))}
          </span>
        ) : (
          <span className={css.placeholder}>{placeholder}</span>
        )}
        {values.length > 0 && (
          <X
            aria-label="Clear all"
            className={css.clearAll}
            onClick={clearAll}
          />
        )}
        <ChevronDown
          className={joinCss(css.chevron, open && css.chevronOpen)}
        />
      </button>

      {open && !usePortal && (
        <div className={css.dropdown}>{dropdownContent}</div>
      )}
      {open &&
        usePortal &&
        portalPosition &&
        createPortal(
          <div
            className={joinCss(css.dropdown, css.dropdownPortal)}
            ref={portalRef}
            style={{
              minWidth: portalPosition.width,
              position: 'fixed',
              ...(portalPosition.alignRight
                ? { right: portalPosition.right }
                : { left: portalPosition.left }),
              ...(portalPosition.placement === 'top'
                ? { bottom: window.innerHeight - portalPosition.top }
                : { top: portalPosition.top }),
            }}
          >
            {dropdownContent}
          </div>,
          document.body
        )}
    </div>
  );
};
